import {
  etapaQueFirma,
  formatearDia,
  formatearFecha,
  formatearFechaHora,
  formatearRevision,
  type Manejo,
  NIVEL_PROTECCION,
  NOMBRE_ETAPA,
  NOMBRE_MANEJO,
} from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Publicar, ReemplazarArchivo } from "../componentes/AccionesDeRevision.tsx";
import { ModalEditarControl, ResumenDeControl } from "../componentes/ControlRegistros.tsx";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { FlujoDeFirmas } from "../componentes/FlujoDeFirmas.tsx";
import { textoBotonFirma } from "../componentes/firmas.ts";
import { HistorialDeRevisiones } from "../componentes/HistorialDeRevisiones.tsx";
import {
  ModalAbrirRevision,
  ModalCambiarDestinatarios,
  ModalCambiarRuta,
  ModalCorregirNombre,
  ModalRetirar,
} from "../componentes/ModalesRevision.tsx";
import { ModalDevolver, ModalFirma } from "../componentes/ModalFirma.tsx";
import {
  Aviso,
  Boton,
  BotonEnlace,
  Dato,
  Enlace,
  EstadoVacio,
  Pildora,
  PildoraSituacion,
  Tarjeta,
} from "../componentes/ui.tsx";
import { TrabajoFallido, Visor } from "../componentes/Visor.tsx";
import type { Detalle } from "../tipos.ts";
import { formatearTamano } from "../utiles.ts";

/** Pantalla 03 · Detalle del documento y flujo de firmas. */
export function PaginaDocumento() {
  const { codigo } = useParams({ from: "/app/documentos/$codigo" });
  const search = useSearch({ from: "/app/documentos/$codigo" });
  const navigate = useNavigate();
  const trpc = useTRPC();
  const detalle = useQuery({
    ...trpc.documentos.detalle.queryOptions({ codigo }),
    // Mientras el worker prepara la vista previa o el sello, se vuelve a preguntar cada 3 s. Se
    // deja de preguntar si el trabajo falló del todo o tras ~10 minutos: ahí ya no es espera.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d || d.trabajoFallido || q.state.dataUpdateCount > 200) return false;
      return hayTrabajoPendiente(d) ? 3000 : false;
    },
  });
  const [modal, setModal] = useState<
    | "firmar"
    | "devolver"
    | "abrir"
    | "ruta"
    | "control"
    | "nombre"
    | "retirar"
    | "destinatarios"
    | null
  >(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // "?firmar" (desde Nuevo documento o la bandeja) abre la firma solo si a esta persona le toca.
  const puedeFirmar = detalle.data?.permisos.firmar;
  useEffect(() => {
    if (!search.firmar || puedeFirmar === undefined) return;
    if (puedeFirmar) setModal((m) => m ?? "firmar");
    else navigate({ to: "/documentos/$codigo", params: { codigo }, search: {}, replace: true });
  }, [search.firmar, puedeFirmar, codigo, navigate]);

  if (detalle.isPending) return <EstadoVacio>Cargando…</EstadoVacio>;
  if (detalle.isError) return <EstadoVacio>{detalle.error.message}</EstadoVacio>;
  const d = detalle.data;
  const doc = d.documento;
  const principal = d.revisiones.find((r) => r.id === d.principalId) ?? null;
  const vigente = d.revisiones.find((r) => r.id === d.revisionVigenteId) ?? null;
  const enCurso = d.revisiones.find((r) => r.id === d.revisionEnCursoId) ?? null;
  const etapaAFirmar = principal ? etapaQueFirma(principal.estado) : null;
  /** Fase 3b: sin ruta de firmas; se publica directo. */
  const informativo = !doc.tipo.requiereAprobacion;
  // Cargada desde el papel o del sistema anterior: vigente sin las firmas de la app.
  const importada = principal?.origen === "importado";
  // El control se edita en la revisión en curso; sin ella, con el permiso temporal, se corrige
  // el de la vigente importada y rige en el acto.
  const revisionDelControl = d.permisos.editarControl
    ? enCurso
    : d.permisos.corregirImportada
      ? vigente
      : null;

  function cerrarModal() {
    setModal(null);
    if (search.firmar) navigate({ to: "/documentos/$codigo", params: { codigo }, search: {} });
  }

  return (
    <div className="max-w-[1180px]">
      <Encabezado
        overline={`${doc.proceso.nombre} › Documentos › ${doc.codigo}`}
        titulo={doc.nombre}
        acciones={
          <>
            {d.permisos.descargarOriginal && principal?.archivo && (
              <Enlace href={`/archivos/${principal.archivo.id}`}>
                Descargar Rev. {formatearRevision(principal.numero)}
              </Enlace>
            )}
            {doc.tipo.esRegistro && (principal?.redactada || principal?.pdfVista) && (
              <BotonEnlace to="/documentos/$codigo/hojas" params={{ codigo }}>
                Hojas llenas
              </BotonEnlace>
            )}
            {principal?.redactada && (
              <BotonEnlace
                to="/documentos/$codigo/editar"
                params={{ codigo }}
                variante={d.permisos.editarContenido ? "primario" : "secundario"}
              >
                {d.permisos.editarContenido ? "Editar contenido" : "Ver contenido"}
              </BotonEnlace>
            )}
            {d.revisiones.length > 1 && (
              <BotonEnlace to="/documentos/$codigo/comparar" params={{ codigo }} search={{}}>
                Ver cambios
              </BotonEnlace>
            )}
            {d.permisos.reemplazarArchivo && principal && (
              <ReemplazarArchivo revisionId={principal.id} onError={setAviso} />
            )}
            {d.permisos.cambiarRuta && (
              <Boton variante="secundario" onClick={() => setModal("ruta")}>
                Cambiar revisor o aprobador
              </Boton>
            )}
            {d.permisos.corregirImportada && (
              <Boton variante="secundario" onClick={() => setModal("nombre")}>
                Corregir nombre
              </Boton>
            )}
            {d.permisos.retirar && (
              <Boton variante="peligro" onClick={() => setModal("retirar")}>
                Retirar documento
              </Boton>
            )}
            {d.permisos.abrirNuevaRevision && principal && (
              <Boton onClick={() => setModal("abrir")}>
                Abrir Rev. {formatearRevision(principal.numero + 1)}
              </Boton>
            )}
            {d.permisos.devolver && (
              <BotonEnlace to="/documentos/$codigo/revisar" params={{ codigo }} variante="primario">
                Revisar y marcar
              </BotonEnlace>
            )}
            {d.permisos.devolver && (
              <Boton variante="peligro" onClick={() => setModal("devolver")}>
                Devolver con observaciones
              </Boton>
            )}
            {d.permisos.publicar && enCurso && (
              <Publicar revisionId={enCurso.id} numero={enCurso.numero} onError={setAviso} />
            )}
            {d.permisos.firmar && principal && etapaAFirmar && (
              <Boton variante="firma" onClick={() => setModal("firmar")}>
                {textoBotonFirma(etapaAFirmar, principal)} Rev.{" "}
                {formatearRevision(principal.numero)}
              </Boton>
            )}
          </>
        }
      />

      {aviso && <Aviso className="mb-4">{aviso}</Aviso>}

      {doc.retiro && (
        <div className="mb-4 rounded-md border border-borde bg-gris-fondo px-4 py-3 text-[12.5px] text-texto-2">
          <span className="font-semibold text-texto">
            Retirado del sistema de gestión el {formatearFecha(doc.retiro.en)} por{" "}
            {doc.retiro.por.nombre}.
          </span>{" "}
          {doc.retiro.motivo} Sus revisiones, firmas y acuses quedan solo para consulta.
        </div>
      )}

      {d.control.faltaUbicacionFisica && d.permisos.editarControl && enCurso && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-ambar-fondo px-3 py-2 text-[12.5px] text-ambar">
          <span>
            Antes de enviar a revisión, indica dónde se archiva la copia física de este registro.
          </span>
          <Boton variante="secundario" onClick={() => setModal("control")}>
            Completar
          </Boton>
        </div>
      )}

      {/* Metadatos de cabecera */}
      <Tarjeta className="mb-5">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Dato etiqueta="Código">
            <span className="font-mono font-semibold text-marino">{doc.codigo}</span>
          </Dato>
          <Dato etiqueta="Revisión en curso">
            {enCurso ? (
              <span>
                {formatearRevision(enCurso.numero)} · <PildoraSituacion situacion={d.situacion} />
              </span>
            ) : (
              <span className="text-texto-3">Ninguna</span>
            )}
          </Dato>
          <Dato etiqueta="Revisión vigente">
            {vigente?.aprobadaEn ? (
              <span>
                {formatearRevision(vigente.numero)} · desde {formatearFecha(vigente.aprobadaEn)}
              </span>
            ) : (
              <span className="text-texto-3">Todavía no hay</span>
            )}
          </Dato>
          <Dato etiqueta="Nivel de protección">
            {doc.nivelProteccion} · {NIVEL_PROTECCION[doc.nivelProteccion].nombre}
          </Dato>
          <Dato etiqueta={doc.tipo.esRegistro ? "Retención" : "Próxima revisión"}>
            {doc.tipo.esRegistro
              ? d.retencion
                ? `Cumple ${formatearDia(d.retencion.cumpleEl)}`
                : "Tras aprobar"
              : doc.proximaRevision
                ? formatearDia(doc.proximaRevision)
                : enCurso
                  ? "Tras aprobar"
                  : "—"}
          </Dato>
          <div className="sm:col-span-2 xl:col-span-3">
            <Dato etiqueta="Motivo del cambio">
              <span className="whitespace-pre-line">{principal?.motivo || "—"}</span>
            </Dato>
          </div>
          <Dato etiqueta="Tipo">{doc.tipo.nombre}</Dato>
          <Dato etiqueta="Manejo">{NOMBRE_MANEJO[doc.manejo as Manejo] ?? doc.manejo}</Dato>
          <div className="sm:col-span-2 xl:col-span-5">
            <Dato etiqueta="Justificación">{doc.justificacion || "—"}</Dato>
          </div>
        </div>
      </Tarjeta>

      {principal && !informativo && !importada && <FlujoDeFirmas revision={principal} yo={d.yo} />}
      {principal && !informativo && importada && (
        <Tarjeta className="mb-5" etiqueta="Aprobada fuera de la app">
          <p className="text-[12.5px] text-texto-2">
            La Rev. {formatearRevision(principal.numero)} entró vigente con la fecha de aprobación
            del papel: sus firmas están en el original, no en GestionDOC. La siguiente revisión, si
            se abre aquí, pasa por las tres firmas.
          </p>
          {principal.firmasPapel.length > 0 && (
            <dl className="mt-3 grid gap-3 sm:grid-cols-3">
              {principal.firmasPapel.map((p) => (
                <div key={p.etapa}>
                  <dt className="text-[10.5px] font-semibold tracking-[.08em] text-texto-3 uppercase">
                    {NOMBRE_ETAPA[p.etapa]} · en papel
                  </dt>
                  <dd className="text-[13px] font-semibold">{p.nombre}</dd>
                  {p.puesto && <dd className="text-[11.5px] text-texto-3">{p.puesto}</dd>}
                </div>
              ))}
            </dl>
          )}
        </Tarjeta>
      )}
      {principal && informativo && !doc.tipo.paraTodaLaEmpresa && (
        <Tarjeta className="mb-5" etiqueta="Documento informativo">
          <p className="text-[12.5px] text-texto-2">
            Se publica sin ruta de firmas ni revisión anual. Lo publica quien lo elabora; el
            personal del proceso recibe el aviso
            {doc.pideAcuse ? " y da acuse de lectura." : "."}
          </p>
        </Tarjeta>
      )}
      {doc.tipo.paraTodaLaEmpresa && (
        <Tarjeta
          className="mb-5"
          etiqueta={
            doc.destinatarios.length === 0
              ? "Comunicado para toda la empresa"
              : `Comunicado para ${doc.destinatarios.length} ${doc.destinatarios.length === 1 ? "persona" : "personas"}`
          }
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="text-[12.5px] text-texto-2">
              {doc.destinatarios.length === 0 ? (
                <p>
                  Al publicarse lo ve y lo recibe todo el personal activo
                  {doc.pideAcuse ? ", y cada persona da acuse de lectura." : "."}
                  {doc.nivelProteccion > 2 && (
                    <span className="block text-ambar">
                      En nivel {doc.nivelProteccion} el alcance vuelve a ser solo el del proceso.
                    </span>
                  )}
                </p>
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {doc.destinatarios.map((p) => (
                    <li
                      key={p.id}
                      className="rounded-full bg-marino-claro px-2.5 py-0.5 text-[12px] text-marino"
                      title={p.puesto ?? undefined}
                    >
                      {p.nombre}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {d.permisos.cambiarDestinatarios && (
              <Boton variante="secundario" onClick={() => setModal("destinatarios")}>
                Cambiar destinatarios
              </Boton>
            )}
          </div>
        </Tarjeta>
      )}

      {principal && (principal.redactada || principal.archivo) && (
        <ResumenDeMarcas codigo={codigo} revisionId={principal.id} />
      )}

      {principal && principal.devoluciones.length > 0 && (
        <Tarjeta titulo="Observaciones de devolución" className="mb-5">
          <ul className="space-y-3">
            {[...principal.devoluciones].reverse().map((x) => (
              <li key={x.id} className="border-l-2 border-rojo pl-3">
                <div className="text-[11.5px] text-texto-3">
                  {NOMBRE_ETAPA[x.etapa]} · {x.usuarioNombre} · {formatearFechaHora(x.creadoEn)}
                </div>
                <div className="text-[13px] whitespace-pre-line">{x.observaciones}</div>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}

      {/* min-w-0: un hijo de grilla no se achica por debajo de su contenido, y el hash de 64
          caracteres (con truncate) empujaba toda la página hacia el costado en el celular. */}
      <div className="grid gap-5 lg:grid-cols-[360px_1fr] [&>*]:min-w-0">
        <Tarjeta titulo={principal?.redactada ? "Contenido" : "Archivo"}>
          {principal?.archivo ? (
            <div>
              <div className="font-mono text-[12.5px] font-semibold break-all text-marino">
                {principal.archivo.nombre}
              </div>
              <div className="text-[11.5px] text-texto-3">
                Original · {formatearTamano(principal.archivo.tamano)}
              </div>
              {d.permisos.descargarOriginal ? (
                <a
                  href={`/archivos/${principal.archivo.id}`}
                  className="mt-2 inline-block text-[12px] font-semibold text-marino hover:underline"
                >
                  Descargar original
                </a>
              ) : (
                d.permisos.descargar && (
                  <div className="mt-2 text-[11.5px] text-texto-3">
                    Nivel {doc.nivelProteccion}: se entrega el PDF con tu nombre; el original solo
                    lo descarga quien lo edita.
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="text-[12.5px] text-texto-3">
              {principal?.redactada ? (
                <>
                  <div>
                    Redactada en el editor
                    {principal.contenidoGuardadoEn
                      ? ` · último guardado ${formatearFechaHora(principal.contenidoGuardadoEn)}`
                      : ""}
                    .
                  </div>
                  {principal.hashContenido && (
                    <div
                      className="mt-1.5 truncate font-mono text-[10.5px]"
                      title={principal.hashContenido}
                    >
                      SHA-256 {principal.hashContenido}
                    </div>
                  )}
                </>
              ) : (
                "Esta revisión no tiene archivo (importada del sistema anterior)."
              )}
            </div>
          )}
          {principal && (principal.archivo || principal.redactada) && (
            <div className="mt-4 border-t border-borde pt-4">
              {principal.pdfSellado ? (
                <div>
                  <div className="flex items-center gap-2">
                    <Pildora tono="bg-verde-fondo text-verde">PDF sellado</Pildora>
                    <span className="font-mono text-[11.5px] text-marino">
                      Sello {principal.idSello}
                    </span>
                  </div>
                  <div
                    className="mt-1.5 truncate font-mono text-[10.5px] text-texto-3"
                    title={principal.hashPdf ?? ""}
                  >
                    SHA-256 {principal.hashPdf}
                  </div>
                  {d.permisos.descargar && (
                    <Enlace
                      variante="primario"
                      href={`/archivos/${principal.pdfSellado.id}`}
                      className="mt-3 inline-block"
                    >
                      Descargar PDF sellado
                    </Enlace>
                  )}
                </div>
              ) : d.trabajoFallido?.que === "sello" ? (
                <TrabajoFallido fallo={d.trabajoFallido} />
              ) : informativo ? (
                <div className="text-[12px] text-texto-3">
                  Documento informativo: se publica sin sello de firmas.
                </div>
              ) : importada ? (
                <div className="text-[12px] text-texto-3">
                  Sin sello digital: aprobada en papel, con las firmas en el original.
                </div>
              ) : principal.estado === "vigente" || principal.estado === "obsoleta" ? (
                <div className="text-[12px] text-ambar">Generando el PDF sellado…</div>
              ) : (
                <div className="text-[12px] text-texto-3">
                  El sello con las tres firmas se aplica al aprobar.
                </div>
              )}
            </div>
          )}
          {d.control.registro && (
            <ResumenDeControl
              control={{ ...d.control, registro: d.control.registro }}
              enCurso={enCurso}
              puedeEditar={!!revisionDelControl}
              onEditar={() => setModal("control")}
            />
          )}
        </Tarjeta>

        <HistorialDeRevisiones revisiones={d.revisiones} puedeDescargar={d.permisos.descargar} />
      </div>

      {principal && (principal.archivo || principal.redactada) && d.permisos.descargar && (
        <Visor
          revision={principal}
          informativo={informativo}
          fallo={d.trabajoFallido?.que === "vista" ? d.trabajoFallido : null}
        />
      )}

      {modal === "firmar" && d.permisos.firmar && principal?.hashContenido && etapaAFirmar && (
        <ModalFirma
          revision={principal}
          hashContenido={principal.hashContenido}
          vigente={vigente}
          etapa={etapaAFirmar}
          onCerrar={cerrarModal}
          onFirmado={() => {
            cerrarModal();
            queryClient.invalidateQueries();
          }}
        />
      )}
      {modal === "abrir" && principal && (
        <ModalAbrirRevision
          documentoId={doc.id}
          codigo={doc.codigo}
          procesoCodigo={doc.proceso.codigo}
          tipoCodigo={doc.tipo.codigo}
          requiereAprobacion={doc.tipo.requiereAprobacion}
          conRevisor={doc.tipo.conRevisor}
          vigente={principal}
          yo={d.yo}
          onCerrar={cerrarModal}
          onAbierta={() => {
            cerrarModal();
            setAviso(null);
          }}
        />
      )}
      {modal === "control" && revisionDelControl && d.control.registro && (
        <ModalEditarControl
          revisionId={revisionDelControl.id}
          corregir={revisionDelControl !== enCurso}
          codigo={doc.codigo}
          nombre={doc.nombre}
          manejoActual={d.control.manejo as Manejo}
          actual={d.control.registro}
          onCerrar={cerrarModal}
          onGuardado={cerrarModal}
        />
      )}
      {modal === "ruta" && principal && (
        <ModalCambiarRuta revision={principal} onCerrar={cerrarModal} onCambiada={cerrarModal} />
      )}
      {modal === "destinatarios" && (
        <ModalCambiarDestinatarios
          documentoId={doc.id}
          actuales={doc.destinatarios.map((p) => p.id)}
          onCerrar={cerrarModal}
          onCambiados={cerrarModal}
        />
      )}
      {modal === "devolver" && principal && (
        <ModalDevolver
          revisionId={principal.id}
          numero={principal.numero}
          onCerrar={cerrarModal}
          onDevuelta={cerrarModal}
        />
      )}
      {modal === "retirar" && vigente && (
        <ModalRetirar
          codigo={doc.codigo}
          nombre={doc.nombre}
          numero={vigente.numero}
          onCerrar={cerrarModal}
          onRetirado={cerrarModal}
        />
      )}
      {modal === "nombre" && vigente && d.permisos.corregirImportada && (
        <ModalCorregirNombre
          revisionId={vigente.id}
          codigo={doc.codigo}
          nombre={doc.nombre}
          onCerrar={cerrarModal}
          onCorregido={cerrarModal}
        />
      )}
    </div>
  );
}

/** Fase 4: cuántas marcas del revisor tiene la revisión, y el camino a la pantalla 09. */
function ResumenDeMarcas({ codigo, revisionId }: { codigo: string; revisionId: string }) {
  const trpc = useTRPC();
  const q = useQuery(trpc.correcciones.listar.queryOptions({ revisionId }));
  const marcas = q.data?.marcas ?? [];
  if (!marcas.length) return null;
  const abiertas = marcas.filter((m) => m.estado === "abierta").length;
  return (
    <Tarjeta className="mb-5" etiqueta="Marcas del revisor">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-[12.5px] text-texto-2">
          {marcas.length} marca{marcas.length === 1 ? "" : "s"} sobre el texto
          {abiertas ? `, ${abiertas} abierta${abiertas === 1 ? "" : "s"}` : ", todas resueltas"}.
        </p>
        <Link
          to="/documentos/$codigo/revisar"
          params={{ codigo }}
          className="text-[12.5px] font-semibold text-marino hover:underline"
        >
          Ver las marcas →
        </Link>
      </div>
    </Tarjeta>
  );
}

/** El worker todavía tiene que convertir el archivo o sellar la revisión. */
function hayTrabajoPendiente(d: Detalle): boolean {
  // Un informativo no se sella; solo espera la conversión del archivo.
  const seSella = d.documento.tipo.requiereAprobacion;
  return d.revisiones.some((r) => {
    // Una importada (cargada desde el papel o del sistema anterior) no se sella en la app.
    const esperaSello =
      seSella && r.origen !== "importado" && r.estado === "vigente" && r.pdfSellado === null;
    return (
      (r.archivo !== null &&
        ((r.pdfVista === null && r.estado !== "obsoleta" && r.estado !== "dispuesto") ||
          esperaSello)) ||
      // Una redactada solo espera al worker por el sello (la vista previa PDF se pide aparte).
      (r.redactada && esperaSello)
    );
  });
}
